import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex, mergeKanbanItemsMembership } from './helpers';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  // Último pedido da lista falhou (rede/servidor). Zera a cada novo pedido.
  listLoadError: false,
  listRequestId: 0,
  // Conversa aberta por link que o SHOW recusou (404/403). A tela explica.
  unavailableConversationId: null,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

// Membership fields are independent of conversation.updated_at ordering.
// Multi-agent participant edits must still refilter Mine/Unassigned when a
// later non-membership event already advanced updated_at in the store.
// kanban_items uses slim/full-safe merge so membership ids never strip a
// ContactPanel-hydrated rich payload.
const applyMembershipFieldsIfPresent = (target, source) => {
  if (!target || !source) return;
  if (Array.isArray(source.participant_user_ids)) {
    target.participant_user_ids = source.participant_user_ids
      .map(Number)
      .filter(Boolean);
  }
  if (Array.isArray(source.kanban_items)) {
    target.kanban_items = mergeKanbanItemsMembership(
      target.kanban_items,
      source.kanban_items
    );
  }
};

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else if (conversation.id !== _state.selectedChatId) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one
        newAllConversations[indexInCurrentList] = conversation;
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        // and never let slim membership kanban_items strip a hydrated full payload.
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = {
          ...conversation,
          allMessagesLoaded: existingConversation.allMessagesLoaded,
          messages: existingConversation.messages,
          dataFetched: existingConversation.dataFetched,
          kanban_items: mergeKanbanItemsMembership(
            existingConversation.kanban_items,
            conversation.kanban_items
          ),
        };
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) return;
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    const existingIds = new Set(chat.messages.map(message => message.id));
    const batchIds = new Set();
    const missingMessages = data.filter(message => {
      if (existingIds.has(message.id) || batchIds.has(message.id)) return false;
      batchIds.add(message.id);
      return true;
    });
    chat.messages.unshift(...missingMessages);
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (!chat) return;

    const nextActivityAt = Number(lastActivityAt);
    if (!Number.isFinite(nextActivityAt) || nextActivityAt <= 0) return;

    // Monotonic: ignore stale/out-of-order echoes so ActionCable + local send
    // cannot oscillate list position when both carry the same event path.
    const currentActivityAt = Number(chat.last_activity_at) || 0;
    if (nextActivityAt < currentActivityAt) return;

    chat.last_activity_at = nextActivityAt;
    // Keep card time and sort key aligned (ConversationCard uses timestamp).
    chat.timestamp = nextActivityAt;
  },

  // Replaces a conversation's kanban_items array. Used by the real-time
  // ActionCable handler so the side panel reflects create/update/destroy
  // without a refetch. The conversation only ever exposes the latest item
  // (see latest_kanban_item in show.json.jbuilder), so we always set the
  // full array — single item or empty.
  [types.UPDATE_CONVERSATION_KANBAN_ITEMS](
    _state,
    { conversationId, kanbanItems }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.kanban_items = kanbanItems;
    }
  },

  // Patch participant_user_ids for Mine/Unassigned refilters without waiting
  // for a full conversation.updated payload (local participant editor path).
  [types.UPDATE_CONVERSATION_PARTICIPANT_USER_IDS](
    _state,
    { conversationId, participantUserIds }
  ) {
    const chat = _state.allConversations.find(c => c.id === conversationId);
    if (!chat) return;
    chat.participant_user_ids = Array.isArray(participantUserIds)
      ? participantUserIds.map(Number).filter(Boolean)
      : [];
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes, deletedCustomAttributes = [] }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
      deletedCustomAttributes.forEach(key => {
        delete conversation.custom_attributes[key];
      });
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      chat.messages.push(message);
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      // SiteUp: o backend grava a atividade da conversa sem callbacks, então
      // NÃO chega um conversation.updated junto desta mensagem. Sem atualizar
      // estes dois campos aqui, a tela só corrige no F5: a sugestão automática
      // (que observa last_non_activity_message) não dispara, e a mensagem do
      // cliente reabre a janela de 24h no servidor com o composer ainda preso
      // em "só template" (can_reply velho).
      if (
        message.message_type !== undefined &&
        message.message_type !== MESSAGE_TYPE.ACTIVITY
      ) {
        chat.last_non_activity_message = message;
      }
      if (message.message_type === MESSAGE_TYPE.INCOMING) {
        chat.can_reply = true;
      }
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  // SiteUp: aplica um message.updated (status / source_id / content_attributes /
  // edição) SOMENTE se a mensagem já está na lista carregada. Ao contrário do
  // ADD_MESSAGE, um UPDATE não deve reinserir uma mensagem que está FORA da janela
  // de paginação — dar push a traria de volta no fim do chat como se fosse "nova"
  // (bug do balão fantasma: ex. quando o ResponseQualityJob re-grava
  // content_attributes de uma resposta antiga, ou um status/leitura tardio chega).
  // Fora da janela = ignora; ela virá com os dados atuais ao rolar/recarregar.
  [types.UPDATE_MESSAGE]({ allConversations }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const index = chat.messages.findIndex(m => m.id === message.id);
    if (index === -1) return;

    chat.messages[index] = message;
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(c => c.id === conversation.id);
    if (!exists) {
      _state.allConversations.push(conversation);
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // Out-of-order guard: ignore stale conversation fields, but never drop
      // multi-agent membership. Participant edits often leave updated_at stagnant
      // while a newer message/status event already advanced the store clock.
      if (conversation.updated_at < selectedConversation.updated_at) {
        applyMembershipFieldsIfPresent(selectedConversation, conversation);
        return;
      }

      const { messages, ...updates } = conversation;
      const previousKanbanItems = selectedConversation.kanban_items;
      allConversations[index] = { ...selectedConversation, ...updates };
      // Membership list/push payloads carry slim kanban_items; preserve any
      // full hydrate already on the conversation when merging.
      if (Array.isArray(updates.kanban_items)) {
        allConversations[index].kanban_items = mergeKanbanItemsMembership(
          previousKanbanItems,
          updates.kanban_items
        );
      }
      // Do not emit SCROLL_TO_MESSAGE here. This mutation merges metadata
      // (custom_attributes, status, assignee, labels) and strips `messages`.
      // New messages already scroll via ADD_MESSAGE. Emitting on every
      // conversation.updated yanked the thread to the bottom when an agent
      // saved a conversation attribute.
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      if (![MENTION, PARTICIPATING].includes(conversationType)) {
        _state.allConversations.push(conversation);
      }
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
    _state.listLoadError = false;
  },

  [types.SET_LIST_LOAD_ERROR](_state, hasError) {
    _state.listLoadError = Boolean(hasError);
  },

  [types.SET_UNAVAILABLE_CONVERSATION](_state, conversationId) {
    _state.unavailableConversationId = conversationId || null;
  },

  [types.SET_LIST_REQUEST_ID](_state, requestId) {
    _state.listRequestId = requestId;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_INBOX](_state, { conversationId, inboxId }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.inbox_id = inboxId;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](
    _state,
    { conversationId, callStatus, callSid }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const message = (chat.messages || []).find(
      m =>
        m.content_type === CONTENT_TYPES.VOICE_CALL &&
        m.call?.provider_call_id === callSid
    );
    if (!message?.call) return;

    message.call = { ...message.call, status: callStatus };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_AI_SUGGESTION](_state, { conversationId, pending }) {
    const chat = _state.allConversations.find(c => c.id === conversationId);
    if (chat) chat.ai_suggestion_pending = pending;
  },
  [types.SET_CONVERSATION_CAN_REPLY](
    _state,
    {
      conversationId,
      canReply,
      messaging_window_expires_at: messagingWindowExpiresAt,
      messaging_window_kind: messagingWindowKind,
      messaging_window_fep_expires_at: messagingWindowFepExpiresAt,
    }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      if (canReply !== undefined) chat.can_reply = canReply;
      if (messagingWindowExpiresAt !== undefined) {
        chat.messaging_window_expires_at = messagingWindowExpiresAt;
      }
      if (messagingWindowKind !== undefined) {
        chat.messaging_window_kind = messagingWindowKind;
      }
      if (messagingWindowFepExpiresAt !== undefined) {
        chat.messaging_window_fep_expires_at = messagingWindowFepExpiresAt;
      }
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
